import React, {memo, useMemo, useState} from 'react'
import {Handle, Position, NodeProps, useNodes, type Node, type Edge} from 'reactflow'
import {toAbsoluteUrl} from '../../../../../../../_metronic/helpers'
import {AUTOMATION_AUDIENCE_OPTIONS} from '../../../../../../Utils/Constant/FlowConstants'

import './TriggerNode.scss'
import {useI18n} from '../../../../../../Utils/_helpers/useI18n'

export const RenderIconNode = ({
  pathIcon,
  className,
  bgColor,
}: {
  pathIcon?: string
  className?: string
  bgColor?: string
}) => {
  return (
    <div
      className={`automation-icon-container ${bgColor ? `triggernode-bg` : ''}`}
      data-bgcolor={bgColor || ''}
    >
      <img src={pathIcon} alt='trigger' className={`automation-icon ${className || ''}`} />
    </div>
  )
}

export interface TriggerNodeProps extends NodeProps {
  pushToUndoStack?: (nodes: Node[], edges: Edge[]) => void
  isUndoRedoOperation?: React.MutableRefObject<boolean>
}

const TriggerNode: React.FC<TriggerNodeProps> = ({
  id,
  selected,
  pushToUndoStack,
  isUndoRedoOperation,
}) => {
  const pathIcon = toAbsoluteUrl('/media/svg/automation/trigger.svg')
  const isSelected = selected
  const nodes = useNodes()
  const node = useMemo(() => nodes.find((n) => n.id === id), [nodes, id])
  const [isHovered, setIsHovered] = useState(false)

  const {t} = useI18n()
  const metaData = (node?.data as {metaData?: any})?.metaData || {}
  const audience = metaData.trigger
  let audienceLabel = audience || ''
  if (audience) {
    const found = AUTOMATION_AUDIENCE_OPTIONS.find((opt) => opt.value === audience)
    if (found && found.titleKey) {
      audienceLabel = t(found.titleKey).toLowerCase()
    } else {
      audienceLabel = audience.toLowerCase()
    }
  }

  let extraList: any[] = []
  let extraType = ''
  if (audience === 'added_tag' && Array.isArray(metaData.tags)) {
    extraList = metaData.tags
    extraType = 'tag'
  }
  if (audience === 'added_segment' && Array.isArray(metaData.segments)) {
    extraList = metaData.segments
    extraType = 'segment'
  }
  const showList = extraList
  const renderTagList = (list: any[]) => {
    return list.map((item, idx) => {
      const isSecondLast = idx === list.length - 2
      const isLast = idx === list.length - 1
      return (
        <React.Fragment key={item.value || item.label || idx}>
          <span
            className='badge bg-light-primary text-primary px-2 py-1 fs-8 border border-primary rounded-2'
            title={item.label || item.value}
            style={{
              maxWidth: 80,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {item.label || item.value}
          </span>
          {!isLast && <span className='mx-1'>and</span>}
        </React.Fragment>
      )
    })
  }

  return (
    <div
      className={`triggernode-root p-2 rounded bg-iite shadow-sm${
        isSelected ? ' border border-2 border-primary' : ''
      }  nopan ${isHovered ? ' node-hover' : ''}`}
      style={{width: 250, minHeight: 90}}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={t('FLOW.NODE.TRIGGER_DESC')}
    >
      <div className='d-flex align-items-center gap-2 mt-4'>
        <div className='automation-icon-container' style={{backgroundColor: 'var(--bs-gray-dark)'}}>
          <img src={toAbsoluteUrl(pathIcon || '')} alt='trigger-icon' className='automation-icon' />
        </div>
        <div className='d-flex flex-column gap-1' style={{maxWidth: 180}}>
          <h4
            className='triggernode-title-text m-0 fw-600 fs-18'
            title={t('FLOW.NODE.TRIGGER_DESC')}
          >
            Trigger
          </h4>
          {audience && (
            <span
              className='fs-6 text-muted'
              title={audienceLabel}
              style={{maxWidth: 180, display: 'inline-block'}}
            >
              <b>{audienceLabel}</b>
            </span>
          )}
          {extraList.length > 0 && (
            <div
              className='d-flex flex-wrap gap-1 mt-1 align-items-center'
              style={{maxWidth: 300, flexWrap: 'wrap'}}
            >
              {renderTagList(showList)}
            </div>
          )}
        </div>
      </div>
      <Handle
        type='source'
        position={Position.Bottom}
        id='trigger-bottom'
        className='triggernode-handle-source'
      />
    </div>
  )
}

export default memo(TriggerNode)
